import {
  Checkbox,
  Dropdown,
  FormItem,
  IconButton,
  Tooltip,
} from '@getsynapse/design-system';
import classNames from 'classnames';
import React from 'react';
import intl from 'react-intl-universal';
import { Option, rangeDateType } from 'utils/customTypes';
import { getInitialValueForDropDown } from 'utils/functions';
import {
  BUDGET_ADDITONAL_FLAGS,
  BUDGET_TABLE_FILTERS,
  BudgetFilters,
  BudgetFiltersKey,
} from '../../../../../../utils/constants/budget';

import { isArrayOfOptions } from '../../../../../../utils/typeGuards';
import useBudgetFilters from '../../../../hooks/useBudgetFilters';

type Props = {
  appliedFilters: BudgetFilters;
  onUpdateFilters: (newFilters: BudgetFilters) => void;
  showAnimation: boolean;
  selectedBudgetId?: string;
};

type UpdateFiltersCallback = (
  key: string,
  value: Option[] | rangeDateType | string
) => void;

const BudgetFiltersForm: React.FC<Props> = ({
  appliedFilters,
  onUpdateFilters,
  showAnimation,
  selectedBudgetId,
}) => {
  const {
    budgetSourceOptions,
    vendorOptions,
    learningTeamOptions,
    projectOptions,
    programOptions,
  } = useBudgetFilters();

  const updateFilters: UpdateFiltersCallback = (key, value) => {
    const updatedFilters: BudgetFilters = { ...appliedFilters };

    if (isArrayOfOptions(value)) {
      if (value.length === 0) {
        delete updatedFilters[key];
      } else {
        updatedFilters[key] = value.map((option) => option.value);
      }
    }

    onUpdateFilters(updatedFilters);
  };

  const getOptionFilters = (
    filters: BudgetFilters,
    filterKey: BudgetFiltersKey,
    value: boolean
  ): BudgetFilters => {
    let filtersCopy = { ...filters };

    if (value) {
      filtersCopy[filterKey] = [BUDGET_ADDITONAL_FLAGS.COST_OF_LABOR];
    } else {
      delete filtersCopy[filterKey];
    }

    return filtersCopy;
  };

  function showShowBudgetAppliedFilters(appliedFilters: BudgetFilters) {
    return String(appliedFilters.budgetId) === selectedBudgetId;
  }

  return (
    <div
      className={classNames('mt-8 flex flex-col space-y-5', {
        'animate-snackarNotification': showAnimation,
      })}
    >
      <FormItem label={intl.get('BUDGET_PAGE.EDIT.FILTERS.PROGRAMS')}>
        <Dropdown
          options={programOptions}
          placeholder={intl.get(
            'BUDGET_PAGE.EDIT.FILTERS.PLACEHOLDER.PROGRAMS'
          )}
          values={
            !showShowBudgetAppliedFilters(appliedFilters)
              ? []
              : getInitialValueForDropDown(
                  programOptions,
                  appliedFilters &&
                    (appliedFilters[BUDGET_TABLE_FILTERS.PROGRAMS] as string[])
                )
          }
          onChange={(option: Option[]) =>
            updateFilters(
              BUDGET_TABLE_FILTERS.PROGRAMS,
              Array.isArray(option) ? option : [option]
            )
          }
        />
      </FormItem>

      <FormItem label={intl.get('BUDGET_PAGE.EDIT.FILTERS.PROJECTS')}>
        <Dropdown
          options={projectOptions}
          placeholder={intl.get(
            'BUDGET_PAGE.EDIT.FILTERS.PLACEHOLDER.PROJECTS'
          )}
          values={
            !showShowBudgetAppliedFilters(appliedFilters)
              ? []
              : getInitialValueForDropDown(
                  projectOptions,
                  appliedFilters &&
                    (appliedFilters[BUDGET_TABLE_FILTERS.PROJECTS] as string[])
                )
          }
          onChange={(option: Option[]) =>
            updateFilters(
              BUDGET_TABLE_FILTERS.PROJECTS,
              Array.isArray(option) ? option : [option]
            )
          }
        />
      </FormItem>

      <FormItem label={intl.get('BUDGET_PAGE.EDIT.FILTERS.VENDORS')}>
        <Dropdown
          options={vendorOptions}
          placeholder={intl.get('BUDGET_PAGE.EDIT.FILTERS.PLACEHOLDER.VENDORS')}
          values={
            !showShowBudgetAppliedFilters(appliedFilters)
              ? []
              : getInitialValueForDropDown(
                  vendorOptions,
                  appliedFilters &&
                    (appliedFilters[BUDGET_TABLE_FILTERS.VENDORS] as string[])
                )
          }
          onChange={(option: Option[]) =>
            updateFilters(
              BUDGET_TABLE_FILTERS.VENDORS,
              Array.isArray(option) ? option : [option]
            )
          }
        />
      </FormItem>

      <FormItem label={intl.get('BUDGET_PAGE.EDIT.FILTERS.TEAMS')}>
        <Dropdown
          options={learningTeamOptions}
          values={
            !showShowBudgetAppliedFilters(appliedFilters)
              ? []
              : getInitialValueForDropDown(
                  learningTeamOptions,
                  appliedFilters &&
                    (appliedFilters[BUDGET_TABLE_FILTERS.TEAMS] as string[])
                )
          }
          placeholder={intl.get('BUDGET_PAGE.EDIT.FILTERS.PLACEHOLDER.TEAMS')}
          onChange={(option: Option[]) =>
            updateFilters(
              BUDGET_TABLE_FILTERS.TEAMS,
              Array.isArray(option) ? option : [option]
            )
          }
        />
      </FormItem>

      <FormItem label={intl.get('BUDGET_PAGE.EDIT.FILTERS.BUDGET_SOURCE')}>
        <Dropdown
          options={budgetSourceOptions}
          values={
            !showShowBudgetAppliedFilters(appliedFilters)
              ? []
              : getInitialValueForDropDown(
                  budgetSourceOptions,
                  appliedFilters &&
                    (appliedFilters[
                      BUDGET_TABLE_FILTERS.BUDGET_SOURCES
                    ] as string[])
                )
          }
          placeholder={intl.get(
            'BUDGET_PAGE.EDIT.FILTERS.PLACEHOLDER.BUDGET_SOURCE'
          )}
          onChange={(option: Option[]) =>
            updateFilters(
              BUDGET_TABLE_FILTERS.BUDGET_SOURCES,
              Array.isArray(option) ? option : [option]
            )
          }
        />
      </FormItem>

      <div className='flex flex-row gap-x-2 items-center'>
        <Checkbox
          label={intl.get('BUDGET_PAGE.INCLUDE_LABOR_COST_TITLE')}
          checked={(
            appliedFilters[BUDGET_TABLE_FILTERS.ADDITIONAL_FLAGS] as string[]
          )?.includes(BUDGET_ADDITONAL_FLAGS.COST_OF_LABOR)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const updatedFilters = getOptionFilters(
              appliedFilters,
              BUDGET_TABLE_FILTERS.ADDITIONAL_FLAGS,
              e.target.checked
            );
            onUpdateFilters(updatedFilters);
          }}
          inputProps={{
            className: 'text-neutral-dark',
          }}
        />

        <Tooltip
          openMode='hover1'
          timeout={0}
          showPopper
          position='botomCenter'
          contentProps={{
            className: 'px-3 py-2 text-sm z-50 absolute',
          }}
          usePortal
          trigger={
            <IconButton
              description=''
              name='information-circle'
              iconClassname='pointer-events-none'
              className='text-lg text-neutral-dark hover:bg-neutral-lightest hover:shadow-allocation-table rounded'
            />
          }
          ariaId='budget-filter-tooltip'
        >
          {intl.get('BUDGET_PAGE.INCLUDE_LABOR_COST_DESCRIPTION')}
        </Tooltip>
      </div>
    </div>
  );
};

export default BudgetFiltersForm;
